import React, { useState, useEffect } from 'react';
import { useCart } from '../context/CartContext';
import { motion } from 'framer-motion';
import { createOrder, authenticated_user, createPaynowPayment } from '../api/apiendpoint';
import CartItemsList from './CartItemsList';
import CustomerForm from './CustomerForm';
import PaymentOptions from './PaymentOptions';
import PayNowModal from './PayNowModal';
import LoadingModal from './LoadingModal';
import EmptyCartMessage from './EmptyCartMessage';
import SuccessModal from './SuccessModal';
import ErrorModal from './ErrorModal';

const CartView = () => {
  const { cartItems, clearCart } = useCart();
  const [customerName, setCustomerName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [address, setAddress] = useState('');
  const [companyName, setCompanyName] = useState('');
  const [loading, setLoading] = useState(false);
  const [paymentMethod, setPaymentMethod] = useState('whatsapp');
  const [paynowStatus, setPaynowStatus] = useState(null);
  const [formErrors, setFormErrors] = useState({});
  const [orderSuccess, setOrderSuccess] = useState(false);
  const [orderError, setOrderError] = useState('');

  const defaultPhoneNumber = '263777160749';
  const DELIVERY_FEE = 3;

  useEffect(() => {
    const fetchUserDetails = async () => {
      try {
        const user = await authenticated_user();
        setCustomerName(`${user.first_name} ${user.last_name}`);
        setEmail(user.email);
        setPhone(user.phone_number || '');
        setAddress(user.address || '');
       
      } catch (error) {
        console.error("Error fetching user data:", error);
      }
    };
    fetchUserDetails();
  }, []);

  const validateForm = () => {
    const errors = {};
    if (!customerName) errors.customerName = 'Full name is required';
    if (!email) errors.email = 'Email is required';
    if (!phone) errors.phone = 'Phone number is required';
    if (!address) errors.address = 'Address is required';

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const subtotal = cartItems.reduce((sum, item) => {
    const price = item.price ?? 0;
    const quantity = item.quantity ?? 1;
    return sum + price * quantity;
  }, 0);

  const totalWithDelivery = subtotal + DELIVERY_FEE;

  const formatWhatsAppMessage = () => {
    let message = `🛒 *Order from ${customerName}*\n`;
    if (companyName) message += `🏢 Company: ${companyName}\n`;
    message += `📧 Email: ${email}\n`;
    if (phone) message += `📞 Phone: ${phone}\n`;
    if (address) message += `🏠 Address: ${address}\n\n`;

    cartItems.forEach((item, index) => {
      const price = item.price ?? 0;
      const quantity = item.quantity ?? 0;
      message += `#${index + 1} ${item.name} - ${item.description || 'No description'} * ${quantity} × $${price.toFixed(2)} = $${(quantity * price).toFixed(2)}\n`;
    });

    message += `\n🚚 Delivery Fee: $${DELIVERY_FEE.toFixed(2)}`;
    message += `\n💵 *Total: $${totalWithDelivery.toFixed(2)}*`;
    return encodeURIComponent(message);
  };

  const submitOrder = async (isPaypal = false, paypalOrderID = null, paypalDetails = null) => {
    setLoading(true);
    setFormErrors({});
    setOrderError('');
    setOrderSuccess(false);

    if (!isPaypal && !validateForm()) {
      setLoading(false);
      return;
    }

    if (cartItems.length === 0) {
      setOrderError('Your cart is empty. Please add items before proceeding.');
      setLoading(false);
      return;
    }

    let orderData;

    try {
      if (isPaypal && paypalDetails) {
        const payer = paypalDetails.payer;
        const shipping = paypalDetails.purchase_units[0].shipping;

        orderData = {
          customer_name: `${payer.name.given_name} ${payer.name.surname}`,
          email: payer.email_address,
          phone_number: payer.phone?.phone_number?.national_number || '',
          address: `${shipping.address.address_line_1}, ${shipping.address.admin_area_2}, ${shipping.address.postal_code}`,
          company_name: companyName || '',
          subtotal: totalWithDelivery,
          delivery_fee: DELIVERY_FEE,
          items: cartItems.map(item => ({
            product_id: item.id,
            name: item.name,
            price: item.price,
            quantity: item.quantity,
            description: item.description || item.product_description || '',
          })),
          payment_method: 'paypal',
          paypal_order_id: paypalOrderID,
        };
      } else {
        orderData = {
          customer_name: customerName,
          email: email,
          phone_number: phone,        // <- Correct key here
          address: address,
          company_name: companyName || '', // <- Correct key here
          subtotal: totalWithDelivery,
          delivery_fee: DELIVERY_FEE,
          items: cartItems.map(item => ({
            product_id: item.id,
            name: item.name,
            price: item.price,
            quantity: item.quantity,
            description: item.description || item.product_discription || '',
          })),
          payment_method: paymentMethod,
        };
      }

      console.log('Submitting orderData:', orderData);

      if (paymentMethod === 'paynow') {
        const response = await createPaynowPayment(orderData);
        setPaynowStatus(response);

        if (response.success) {
          clearCart();
          setOrderSuccess(true);
        } else {
          throw { response: { data: response } };
        }
      } else if (isPaypal) {
        await createOrder(orderData);
        clearCart();
        setOrderSuccess(true);
      } else {
        await createOrder(orderData);
        const message = formatWhatsAppMessage();
        const url = `https://wa.me/${defaultPhoneNumber}?text=${message}`;
        clearCart();
        window.open(url, '_blank');
        setOrderSuccess(true);
      }
    } catch (err) {
      console.error('Order processing error:', err);
      let errorMessage = 'Failed to process order. Please try again.';

      if (err.response?.data?.error) {
        errorMessage = err.response.data.error;
      } else if (err.response?.data?.message) {
        errorMessage = err.response.data.message;
      } else if (err.error) {
        errorMessage = err.error;
      } else if (err.message) {
        errorMessage = err.message;
      }

      setOrderError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <br /><br /><br />
      <motion.div className="container mt-5"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}>

        {loading && <LoadingModal />}
        {orderSuccess && <SuccessModal onClose={() => setOrderSuccess(false)} />}
        {orderError && <ErrorModal message={orderError} onClose={() => setOrderError('')} />}

        <h2 className="mb-4 text-center">🛒 Your Shopping Cart</h2>

        {cartItems.length === 0 ? (
          <EmptyCartMessage />
        ) : (
          <>
            <CartItemsList />

            <div className="alert alert-info text-center mt-3">
              🚚 Flat delivery fee of $3.00 applies.<br />
              📍 Coverage: 15km radius from Highlands Park, Harare.
            </div>

            <motion.div className="row mt-5" initial={{ opacity: 0, y: 40 }} animate={{ opacity: 1, y: 0 }}>
              {(paymentMethod === 'whatsapp' || paymentMethod === 'paynow') && (
                <CustomerForm 
                  customerName={customerName}
                  setCustomerName={setCustomerName}
                  email={email}
                  setEmail={setEmail}
                  phone={phone}
                  setPhone={setPhone}
                  address={address}
                  setAddress={setAddress}
                  companyName={companyName}
                  setCompanyName={setCompanyName}
                  formErrors={formErrors}
                />
              )}

              <PaymentOptions 
                subtotal={totalWithDelivery}
                paymentMethod={paymentMethod}
                setPaymentMethod={setPaymentMethod}
                submitOrder={submitOrder}
                loading={loading}
                cartItems={cartItems}
              />
            </motion.div>

            {paynowStatus && (
              <PayNowModal 
                paynowStatus={paynowStatus}
                setPaynowStatus={setPaynowStatus}
              />
            )}
          </>
        )}
      </motion.div>
    </>
  );
};

export default CartView;
